/**
 * LinkModal Component
 * Reusable modal for displaying an external link in an iframe (e.g. article link).
 * Same UX pattern as VideoModal: DefaultModal + iframe.
 */

import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../constants/colors'
import { DefaultModal } from '../../shared/components/DefaultModal'

export interface LinkModalProps {
  isOpen: boolean
  onClose: () => void
  url: string
  title: string
  description?: string
  className?: string
}

/**
 * LinkModal - Opens a URL in an iframe inside a modal (for article external links)
 */
export const LinkModal: React.FC<LinkModalProps> = ({ isOpen, onClose, url, title, description, className = '' }) => {
  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
      showCloseButton={true}
      maxWidth="800px"
      backgroundColor={INTERAX_COLORS.offBlack}
    >
      {title && (
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.headline4,
            color: INTERAX_COLORS.white,
            mb: 2
          }}
        >
          {title}
        </Typography>
      )}

      <Box
        sx={{
          position: 'relative',
          pb: '56.25%',
          height: 0,
          overflow: 'hidden',
          mb: 3
        }}
      >
        <Box
          component="iframe"
          src={url}
          title={title}
          allow="fullscreen"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: INTERAX_COLORS.offBlack
          }}
        />
      </Box>

      {description && (
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body2,
            color: INTERAX_COLORS.white,
            mb: 3
          }}
        >
          {description}
        </Typography>
      )}
    </DefaultModal>
  )
}
